import React, { useState, useEffect, useCallback } from "react";
import { Bot, User, Loader2, AlertCircle, CheckCircle, LayoutDashboard } from "lucide-react";
import { cn } from "@/lib/utils";
import ReactMarkdown, { Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { useSmoothText, type UIMessage } from "@convex-dev/agent/react";
import { useAgentCanvas } from "@/contexts/AgentCanvasContext";
import { Button } from "@/components/ui/button";
// Bars Loader Component - Animated vertical bars loading indicator
const BarsLoader = ({ size = 16, className }: { size?: number; className?: string }) => (
  <svg
    height={size}
    viewBox="0 0 24 24"
    width={size}
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <title>Loading...</title>
    <style>{`
      .spinner-bar {
        animation: spinner-bars-animation .8s linear infinite;
        animation-delay: -.8s;
      }
      .spinner-bars-2 {
        animation-delay: -.65s;
      }
      .spinner-bars-3 {
        animation-delay: -0.5s;
      }
      @keyframes spinner-bars-animation {
        0% {
          y: 1px;
          height: 22px;
        }
        93.75% {
          y: 5px;
          height: 14px;
          opacity: 0.2;
        }
      }
    `}</style>
    <rect className="spinner-bar" fill="currentColor" height="22" width="6" x="1" y="1" />
    <rect
      className="spinner-bar spinner-bars-2"
      fill="currentColor"
      height="22"
      width="6"
      x="9"
      y="1"
    />
    <rect
      className="spinner-bar spinner-bars-3"
      fill="currentColor"
      height="22"
      width="6"
      x="17"
      y="1"
    />
  </svg>
);

interface StreamingMessageProps {
  message: UIMessage;
  className?: string;
  theme?: "blue" | "purple"; // Theme for message bubbles and avatars
  threadId?: string; // Thread ID for feedback tracking
  agentType?: "business-assistant" | "document-writer"; // Agent type for feedback
  showFeedback?: boolean; // Whether to show feedback buttons (default: true)
}

interface StreamingState {
  content: string;
  displayedContent: string;
  isActive: boolean;
  isComplete: boolean;
  error: string | null;
  chunkCount: number;
}

// Factory function to create markdown components with theme support
const createMarkdownComponents = (
  theme: "blue" | "purple" | "orange" = "blue",
  isStreaming: boolean = false,
  updateCanvas: (
    content: string,
    streaming: boolean,
    messageId?: string,
    shouldAutoOpen?: boolean
  ) => void,
  openCanvas: (content: string, streaming: boolean) => void,
  messageId?: string,
  isNewMessage: boolean = true,
  isStopped: boolean = false
) => {
  const linkColor =
    theme === "purple"
      ? "text-purple-600 hover:text-purple-800"
      : theme === "orange"
        ? "text-orange-600 hover:text-orange-800"
        : "text-blue-600 hover:text-blue-800";

  return {
    // Headers
    h1: ({ children }: { children: React.ReactNode }) => (
      <h1 className="mb-3 mt-4 text-lg font-bold text-gray-900 first:mt-0">{children}</h1>
    ),
    h2: ({ children }: { children: React.ReactNode }) => (
      <h2 className="mb-2 mt-3 text-base font-bold text-gray-900 first:mt-0">{children}</h2>
    ),
    h3: ({ children }: { children: React.ReactNode }) => (
      <h3 className="mb-2 mt-3 text-sm font-bold text-gray-900 first:mt-0">{children}</h3>
    ),
    h4: ({ children }: { children: React.ReactNode }) => (
      <h4 className="mb-1 mt-2 text-sm font-semibold text-gray-900 first:mt-0">{children}</h4>
    ),
    h5: ({ children }: { children: React.ReactNode }) => (
      <h5 className="mb-1 mt-2 text-sm font-semibold text-gray-800 first:mt-0">{children}</h5>
    ),
    h6: ({ children }: { children: React.ReactNode }) => (
      <h6 className="mb-1 mt-2 text-sm font-medium text-gray-800 first:mt-0">{children}</h6>
    ),

    // Paragraphs
    p: ({ children }: { children: React.ReactNode }) => (
      <p className="mb-2 text-sm leading-relaxed text-gray-900 last:mb-0">{children}</p>
    ),

    // Lists
    ul: ({ children }: { children: React.ReactNode }) => (
      <ul className="mb-2 list-inside list-disc space-y-1 text-sm text-gray-900">{children}</ul>
    ),
    ol: ({ children }: { children: React.ReactNode }) => (
      <ol className="mb-2 list-inside list-decimal space-y-1 text-sm text-gray-900">{children}</ol>
    ),
    li: ({ children }: { children: React.ReactNode }) => (
      <li className="text-sm text-gray-900">{children}</li>
    ),

    // Code
    code: ({ children, className }: { children: React.ReactNode; className?: string }) => {
      const match = /language-(\w+)/.exec(className || "");
      const language = match ? match[1] : "";
      const isInline = !match;

      // Intercept HTML or Canvas code blocks for Generative UI
      if (!isInline && (language === "html" || language === "canvas")) {
        const htmlStr = String(children).replace(/\n$/, "");

        // Use a side-effect to update the global canvas context
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
          if (!isStopped) {
            updateCanvas(htmlStr, isStreaming, messageId, isNewMessage);
          }
        }, [htmlStr, isStreaming, messageId, isNewMessage, isStopped]);

        // Determine document type and title for the UI box
        let boxTitle = "Interactive Canvas";
        let boxSubtitle = "Interactive Canvas Output";

        // Attempt to extract title from HTML (<title>, <h1>, <h2>, or data-title)
        const titleMatch =
          /<title[^>]*>([\s\S]*?)<\/title>/i.exec(htmlStr) ||
          /<h1[^>]*>([\s\S]*?)<\/h1>/i.exec(htmlStr) ||
          /<h2[^>]*>([\s\S]*?)<\/h2>/i.exec(htmlStr);

        if (titleMatch && titleMatch[1]) {
          const cleanTitle = titleMatch[1]
            .replace(/<[^>]*>/g, "")
            .replace(/&amp;/g, "&")
            .replace(/&lt;/g, "<")
            .replace(/&gt;/g, ">")
            .replace(/&quot;/g, '"')
            .replace(/&#39;/g, "'")
            .trim();
          if (cleanTitle && cleanTitle.length >= 2 && cleanTitle.length <= 60) {
            boxTitle = cleanTitle;
            boxSubtitle = "Interactive Canvas";
          }
        }

        if (boxTitle === "Interactive Canvas") {
          const contentLower = htmlStr.toLowerCase();
          if (
            contentLower.includes("report") ||
            contentLower.includes("breakdown") ||
            contentLower.includes("kpi") ||
            contentLower.includes("chart") ||
            contentLower.includes("dashboard") ||
            contentLower.includes("analytics")
          ) {
            boxTitle = "Interactive Report";
            boxSubtitle = "View interactive report";
          } else if (contentLower.includes("proposal")) {
            boxTitle = "Interactive Proposal";
            boxSubtitle = "View your proposal";
          } else if (contentLower.includes("invoice")) {
            boxTitle = "Interactive Invoice";
            boxSubtitle = "View your invoice";
          } else if (
            contentLower.includes("service agreement") ||
            contentLower.includes("cleaning agreement") ||
            contentLower.includes("services agreement") ||
            contentLower.includes("master service")
          ) {
            boxTitle = "Service Agreement";
            boxSubtitle = "View your agreement";
          } else if (contentLower.includes("slide") || contentLower.includes("presentation")) {
            boxTitle = "Interactive Presentation";
            boxSubtitle = "View presentation";
          } else if (contentLower.includes("flyer") || contentLower.includes("brochure")) {
            boxTitle = "Interactive Flyer";
            boxSubtitle = "View flyer";
          }
        }

        return (
          <div className="my-3 flex w-full flex-col gap-3 rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="shrink-0 rounded-md bg-blue-50 p-2 text-blue-600">
                <LayoutDashboard className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <h4 className="truncate text-sm font-medium text-gray-900">{boxTitle}</h4>
                <p className="truncate text-xs text-gray-500">{boxSubtitle}</p>
              </div>
            </div>
            <Button
              size="sm"
              className="w-full shrink-0"
              onClick={() => openCanvas(htmlStr, isStreaming)}
            >
              Open Canvas
            </Button>
          </div>
        );
      }

      if (isInline) {
        return (
          <code className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-sm text-gray-800">
            {children}
          </code>
        );
      }
      return (
        <code className="block overflow-x-auto whitespace-pre rounded-md bg-gray-100 p-3 font-mono text-sm text-gray-800">
          {children}
        </code>
      );
    },

    // Code blocks
    pre: ({ children }: { children: React.ReactNode }) => {
      // If the child is our custom HTML canvas code block, do NOT wrap it in a <pre>
      // because <pre> forces whitespace-pre and overflow-x-auto, breaking our responsive card.
      const isReactElement = React.isValidElement(children);
      if (isReactElement && children.props && typeof children.props.className === "string") {
        const className = children.props.className;
        if (className.includes("language-html") || className.includes("language-canvas")) {
          return <div className="my-2 w-full">{children}</div>;
        }
      }

      return (
        <pre className="mb-3 mt-2 overflow-x-auto rounded-md bg-gray-100 p-3 font-mono text-sm text-gray-800">
          {children}
        </pre>
      );
    },

    // Emphasis
    strong: ({ children }: { children: React.ReactNode }) => (
      <strong className="font-bold text-gray-900">{children}</strong>
    ),
    em: ({ children }: { children: React.ReactNode }) => (
      <em className="italic text-gray-900">{children}</em>
    ),

    // Links - Theme-aware
    a: ({ href, children }: { href?: string; children: React.ReactNode }) => (
      <a href={href} target="_blank" rel="noopener noreferrer" className={`${linkColor} underline`}>
        {children}
      </a>
    ),

    // Blockquotes
    blockquote: ({ children }: { children: React.ReactNode }) => (
      <blockquote className="my-2 rounded-r border-l-4 border-gray-300 bg-gray-50 py-2 pl-4 text-sm italic text-gray-700">
        {children}
      </blockquote>
    ),

    // Horizontal rule
    hr: () => <hr className="my-4 border-gray-300" />,

    // Tables
    table: ({ children }: { children: React.ReactNode }) => (
      <div className="my-3 overflow-x-auto">
        <table className="min-w-full rounded-md border border-gray-300">{children}</table>
      </div>
    ),
    thead: ({ children }: { children: React.ReactNode }) => (
      <thead className="bg-gray-100">{children}</thead>
    ),
    tbody: ({ children }: { children: React.ReactNode }) => (
      <tbody className="bg-white">{children}</tbody>
    ),
    tr: ({ children }: { children: React.ReactNode }) => (
      <tr className="border-b border-gray-200">{children}</tr>
    ),
    th: ({ children }: { children: React.ReactNode }) => (
      <th className="border-r border-gray-300 px-3 py-2 text-left text-sm font-semibold text-gray-900 last:border-r-0">
        {children}
      </th>
    ),
    td: ({ children }: { children: React.ReactNode }) => (
      <td className="border-r border-gray-300 px-3 py-2 text-sm text-gray-900 last:border-r-0">
        {children}
      </td>
    ),
  };
};

/**
 * StreamingMessage Component
 *
 * ✅ OFFICIAL CONVEX AGENT PATTERN
 * Uses the official Convex Agent useSmoothText hook for typewriter effect.
 * Reference: https://github.com/get-convex/agent/blob/main/example/ui/chat/ChatStreaming.tsx
 */
export function StreamingMessage({
  message,
  className,
  theme = "blue",
  threadId,
  agentType,
  showFeedback = true,
  onUpdate,
}: {
  message: UIMessage;
  className?: string;
  theme?: "blue" | "purple" | "orange";
  threadId?: string;
  agentType?: string;
  showFeedback?: boolean;
  onUpdate?: () => void;
}) {
  const isUser = message.role === "user";
  const isStopped = message.status === "failed" && (message as any).error === "Stopped by user";
  const isComplete = message.status === "success" || message.status === "failed";

  // ✅ OFFICIAL PATTERN: Pass message.text directly to useSmoothText
  // The hook handles all streaming logic automatically
  const text = message.text || "";
  const hasCanvasBlock = text.includes("```html") || text.includes("```canvas");
  
  const [visibleText, smoothState] = useSmoothText(text, {
    // This tells the hook that it's ok to start streaming immediately.
    // If this was always passed as true, messages that are already done would
    // also stream in.
    // If this was always passed as false (default), then the streaming message
    // wouldn't start streaming until the second chunk was received.
    startStreaming: message.status === "streaming",
  });

  // A message is actively streaming if either the backend is still generating it,
  // OR the visual typewriter effect is still catching up and revealing text.
  // FIX: If the message contains a massive HTML block, bypass the typewriter effect's state
  // so the canvas doesn't hang for minutes waiting for invisible HTML to be "typed" out.
  const isActivelyStreaming = hasCanvasBlock 
    ? message.status === "streaming"
    : message.status === "streaming" || smoothState.isStreaming;
    
  // Use raw text for canvas blocks so the HTML is available to the canvas at network speed.
  const contentToRender = hasCanvasBlock ? text : visibleText;

  // Determine if this is a newly generated message in this session or a historical one.
  // Historical messages load with status "success" (or "failed") immediately.
  // Newly generated messages start with status "streaming".
  const isNewMessageRef = React.useRef(message.status === "streaming");

  const { updateCanvasContent, openCanvas } = useAgentCanvas();

  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (!isActivelyStreaming) {
      setIsPaused(false);
      return;
    }
    setIsPaused(false);
    const timer = setTimeout(() => setIsPaused(true), 500);
    return () => clearTimeout(timer);
  }, [text, isActivelyStreaming]);

  // Memoize components to prevent remounting of custom markdown components (like code) on every stream tick
  const markdownComponents = React.useMemo(() => {
    return createMarkdownComponents(
      theme,
      isActivelyStreaming,
      updateCanvasContent,
      openCanvas,
      message.key as string,
      isNewMessageRef.current,
      isStopped
    );
  }, [theme, isActivelyStreaming, updateCanvasContent, openCanvas, message.key, isStopped]);

  // Call onUpdate whenever visibleText changes so parent can scroll
  useEffect(() => {
    if (onUpdate) {
      onUpdate();
    }
  }, [visibleText, onUpdate]);

  console.log("🎬 StreamingMessage render:", {
    messageKey: message.key,
    role: message.role,
    status: message.status,
    textLength: text.length,
    visibleTextLength: (visibleText || "").length,
    isStreaming: message.status === "streaming",
    isActivelyStreaming,
  });

  // Render message content with progressive text using visibleText from useSmoothText
  const renderContent = () => {
    // ✅ Use contentToRender which bypasses typewriter effect for canvas blocks
    const content = contentToRender;

    // Show thinking indicator when there's no content yet
    if (!content && !isUser && message.status !== "success" && message.status !== "failed") {
      return (
        <div className="flex items-center gap-3">
          <BarsLoader
            size={18}
            className={cn(
              theme === "purple"
                ? "text-purple-600"
                : theme === "orange"
                  ? "text-orange-600"
                  : "text-blue-600"
            )}
          />
          <span className="text-xs font-bold uppercase tracking-widest text-gray-400">
            Thinking...
          </span>
        </div>
      );
    }

    // Show error state
    if (message.status === "failed") {
      return (
        <div className="space-y-2">
          <div
            className={cn(
              "flex items-center gap-2 rounded-md p-2",
              isStopped ? "bg-amber-50 text-amber-600" : "bg-red-50 text-red-500"
            )}
          >
            <AlertCircle className="h-4 w-4" />
            <span className="text-sm">
              {isStopped ? "Generation stopped" : "Failed to generate response"}
            </span>
          </div>
          {content && (
            <div className="text-gray-600">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={markdownComponents as Components}
              >
                {content}
              </ReactMarkdown>
            </div>
          )}
        </div>
      );
    }

    // For user messages, render as plain text (no markdown processing)
    if (isUser) {
      return <div className="whitespace-pre-wrap text-sm">{content || ""}</div>;
    }

    // For assistant messages, use markdown rendering with theme-aware components
    return (
      <div className="prose prose-sm max-w-none">
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents as Components}>
          {content}
        </ReactMarkdown>
      </div>
    );
  };

  return (
    <div className={cn("flex flex-col", className)}>
      {/* Message Row */}
      <div className={cn("flex gap-3", isUser ? "justify-end" : "justify-start")}>
        {/* Assistant Avatar */}
        {!isUser && (
          <div
            className={cn(
              "flex-shrink-0 self-start rounded-lg p-2",
              theme === "purple"
                ? "bg-purple-100"
                : theme === "orange"
                  ? "bg-orange-100"
                  : "bg-blue-100"
            )}
          >
            <Bot
              className={cn(
                "h-4 w-4",
                theme === "purple"
                  ? "text-purple-600"
                  : theme === "orange"
                    ? "text-orange-600"
                    : "text-blue-600"
              )}
            />
          </div>
        )}

        {/* Message Content Wrapper */}
        <div className="flex flex-col max-w-[80%] gap-1">
          <div
            className={cn(
              "rounded-lg",
              // Only add padding and background if not showing thinking indicator
              !contentToRender && !isUser ? "" : "p-3",
              // Vertically center the thinking indicator with the bot avatar
              !contentToRender && !isUser ? "self-center" : "",
              isUser
                ? theme === "purple"
                  ? "ml-auto bg-purple-600 text-white"
                  : theme === "orange"
                    ? "ml-auto bg-orange-600 text-white"
                    : "ml-auto bg-blue-600 text-white"
                : !contentToRender
                  ? ""
                  : "bg-gray-100 text-gray-900",
              {
                "bg-green-100": isActivelyStreaming && contentToRender,
                "bg-red-100": message.status === "failed",
                "ring-1 ring-orange-200": theme === "orange" && !isUser && contentToRender,
              }
            )}
          >
            {renderContent()}
          </div>
        </div>

        {/* User Avatar */}
        {isUser && (
          <div
            className={cn(
              "flex-shrink-0 self-start rounded-lg p-2",
              theme === "purple"
                ? "bg-purple-600"
                : theme === "orange"
                  ? "bg-orange-600"
                  : "bg-blue-600"
            )}
          >
            <User className="h-4 w-4 text-white" />
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Hook for managing streaming message state
 * This will be enhanced to integrate with the Convex streaming system
 */
export function useStreamingMessage(messageId: string) {
  const [streamingState, setStreamingState] = useState<StreamingState>({
    content: "",
    displayedContent: "",
    isActive: false,
    isComplete: false,
    error: null,
    chunkCount: 0,
  });

  const startStreaming = useCallback(() => {
    setStreamingState((prev) => ({
      ...prev,
      isActive: true,
      isComplete: false,
      error: null,
    }));
  }, []);

  const updateContent = useCallback((newContent: string) => {
    setStreamingState((prev) => ({
      ...prev,
      content: newContent,
      chunkCount: prev.chunkCount + 1,
    }));
  }, []);

  const completeStreaming = useCallback(() => {
    setStreamingState((prev) => ({
      ...prev,
      isActive: false,
      isComplete: true,
    }));
  }, []);

  const setError = useCallback((error: string) => {
    setStreamingState((prev) => ({
      ...prev,
      isActive: false,
      error,
    }));
  }, []);

  return {
    streamingState,
    startStreaming,
    updateContent,
    completeStreaming,
    setError,
  };
}

export default StreamingMessage;
